// src/portal-app/tabs/OperationsTab.js
//
// P1 — Digest Scroll. The old 2x2 grid told you nothing; this screen's job
// is to tell you what needs you TODAY. The verdict sentence at the top is
// the whole point of the pattern (spec §5 P1) and it is binding: a comparison
// or a consequence, or nothing at all. See buildVerdictSentence below — do
// not add fallback copy so the card "doesn't look empty" when no branch
// matches. VerdictCard already renders stats + sparkline alone for exactly
// that case.
import React from 'react';
import { useLanguage } from '../../context/LanguageContext';
import { useAuth } from '../../context/AuthContext';
import { useNav } from '../nav/NavStack';
import { useHubCards } from '../nav/useHubCards';
import {
    VerdictCard, AttentionList, CondensedRow, ShareBar, ensureKitCss,
} from '../shell/kit';
import { fmtMoney } from '../insights/kit';
import { useOperationsData } from './data/useOperationsData';
import { ensureTabCss } from './tabCss';
import { HubEmptyState } from './HubEmptyState';

/* ---- local icon set — matching TeamTab.js's precedent of a card-specific
   icon set kept local to the screen that consumes it, rather than growing
   the shared kit set for four consumers. Inline SVG only, never emoji
   (spec §4). ---- */
const IconTransactions = () => (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor"
        strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
        <path d="M5 2.8h14v18.4l-2.4-1.8-2.3 1.8-2.3-1.8-2.3 1.8-2.3-1.8-2.4 1.8Z" />
        <path d="M8.5 8h7M8.5 12h5" />
    </svg>
);

const IconReservations = () => (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor"
        strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
        <rect x="3.5" y="5" width="17" height="15.5" rx="2.5" />
        <path d="M3.5 9.8h17" />
        <path d="M8 2.8v3.6M16 2.8v3.6" />
    </svg>
);

const IconExpenses = () => (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor"
        strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
        <path d="M3.5 7a2.5 2.5 0 0 1 2.5-2.5h11.5M3.5 7v10.5a2.5 2.5 0 0 0 2.5 2.5h12a2.5 2.5 0 0 0 2.5-2.5V9.8a2.5 2.5 0 0 0-2.5-2.5H3.5" />
        <circle cx="16.4" cy="13.6" r="1.4" fill="currentColor" stroke="none" />
    </svg>
);

const IconEod = () => (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor"
        strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
        <circle cx="12" cy="12" r="8.5" />
        <path d="M12 7.5V12l3.2 2" />
    </svg>
);

const LockIcon = () => (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor"
        strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
        <path d="M8 11V7a4 4 0 0 1 8 0v4" />
        <rect x="3" y="11" width="18" height="11" rx="2" ry="2" />
    </svg>
);

const CARD_ICON = {
    transactions: IconTransactions,
    reservations: IconReservations,
    expenses: IconExpenses,
    eod: IconEod,
};
const CARD_TONE = {
    transactions: 'blue', reservations: 'teal', expenses: 'purple', eod: 'amber',
};

// Verdict copy rule (spec §5 P1 — BINDING). Four branches, evaluated in
// order; the fourth is "no sentence at all" — never a status platitude like
// "Service is running smoothly". `pct` is the absolute value rounded to one
// decimal; the direction is already carried by the wording ("ahead of" /
// "below"), so no sign is rendered inside the sentence.
function buildVerdictSentence(data, t) {
    const changePct = Number(data.revenueChangePct) || 0;
    const pct = Math.abs(changePct).toFixed(1);

    if (changePct >= 10) {
        return t('verdict_ops_up', { pct }, 'Takings are running {pct}% ahead of yesterday.');
    }
    if (changePct <= -10) {
        return t('verdict_ops_down', { pct }, 'Takings are running {pct}% below yesterday.');
    }
    if (data.openTills > 0) {
        return t(
            'verdict_ops_tills',
            { n: data.openTills },
            'Trading is steady. {n} till(s) still open from earlier.',
        );
    }
    return undefined;
}

function lockedRow(card, t) {
    return {
        icon: <LockIcon />,
        tone: CARD_TONE[card.id],
        name: t(card.labelKey, {}, card.fallback),
        value: <span className="tab-locked-hint">{t('hub_locked_hint', {}, 'Upgrade to unlock')}</span>,
        onPress: undefined,
    };
}

// Only transactions (orders) and eod (openTills) have a value in `data` —
// reservations and expenses get an em dash, per the brief (correctness
// point: dig-deeper values are drawn from data where one exists, '—'
// otherwise). data.orders can be `null` (its secondary call failed) even
// while the screen is otherwise ready — that must read '—', not the literal
// string "null".
// NOTE: tappability is blocked TWICE here — the `state === 'locked'` early
// return above (which substitutes lockedRow's whole shape) AND the
// `state === 'active'` ternary on `onPress` below (a locked card is never
// 'active', so onPress stays undefined even without the early return). The
// two guards are redundant by construction, not by accident: a mutation
// test that removes only the early return still leaves the row non-tappable
// via the ternary. Don't assume removing one of them is safe on its own —
// verify both are gone before trusting a "not tappable" test again.
function buildDigRow(card, data, t, push) {
    if (card.state === 'locked') return lockedRow(card, t);
    const Icon = CARD_ICON[card.id];
    let value = '—';
    if (card.id === 'transactions') value = data.orders == null ? '—' : data.orders;
    if (card.id === 'eod') value = data.openTills;
    return {
        icon: <Icon />,
        tone: CARD_TONE[card.id],
        name: t(card.labelKey, {}, card.fallback),
        value,
        onPress: card.state === 'active' ? () => push(card.id) : undefined,
    };
}

export default function OperationsTab() {
    ensureKitCss();
    ensureTabCss();
    const { t } = useLanguage();
    const { getCurrency } = useAuth() || {};
    const { push } = useNav();
    const { status, data, reload } = useOperationsData();
    const cards = useHubCards('operations');

    // Local calendar day, never UTC/toISOString — a portal on the west side
    // of midnight UTC must not show tomorrow's date.
    const dateLine = new Date().toLocaleDateString(undefined, {
        weekday: 'long', day: 'numeric', month: 'long',
    });

    const header = (
        <>
            <h1 className="tab-title">{t('tab_operations', {}, 'Operations')}</h1>
            <p className="tab-sub">{dateLine}</p>
        </>
    );

    if (status === 'loading') {
        return (
            <div className="tab-wrap">
                {header}
                <div data-testid="operations-skeleton" aria-hidden="true">
                    <div className="tab-skel" style={{ height: 150 }} />
                    <div className="tab-skel" style={{ height: 100 }} />
                    <div className="tab-skel" style={{ height: 220 }} />
                </div>
            </div>
        );
    }

    if (status === 'error') {
        return (
            <div className="tab-wrap">
                {header}
                <div className="tab-state" data-testid="operations-error">
                    <p className="tab-empty-title">{t('operations_error_title', {}, "Couldn't load operations")}</p>
                    <p className="tab-empty-msg">
                        {t('operations_error_msg', {}, 'Check your connection and try again.')}
                    </p>
                    <button type="button" className="tab-retry" onClick={reload}>
                        {t('retry', {}, 'Retry')}
                    </button>
                </div>
            </div>
        );
    }

    const currency = getCurrency ? getCurrency() : '£';

    const cardById = {};
    cards.forEach((c) => { cardById[c.id] = c; });
    const eodActive = cardById.eod && cardById.eod.state === 'active';
    const transactionsActive = cardById.transactions && cardById.transactions.state === 'active';
    const reservationsActive = cardById.reservations && cardById.reservations.state === 'active';

    const sentence = buildVerdictSentence(data, t);
    const stats = [
        {
            value: data.revenue == null ? '—' : fmtMoney(data.revenue, currency),
            caption: t('taken_today', {}, 'taken today'),
        },
        { value: data.orders == null ? '—' : String(data.orders), caption: t('transactions_count', {}, 'transactions') },
    ];

    // Cash/card come from useOperationsData's secondary tx summary and are
    // `null` together with `orders`/`revenue` when that call failed — a 0/0
    // split (or a null one) is meaningless, not "50/50", so the bar is
    // omitted rather than rendered empty (correctness point 4).
    const cash = data.cash || 0;
    const card = data.card || 0;
    const showShareBar = cash + card > 0;

    // AttentionList rows — `null` means the feature is off or its call
    // failed and the row must be ABSENT, not rendered as zero. A single
    // `> 0` guard covers both `0` and `null` (null > 0 is false).
    const attentionItems = [];
    if (data.openTills > 0) {
        attentionItems.push({
            id: 'tills',
            tone: 'red',
            text: t('n_tills_open', { n: data.openTills }, '{n} till(s) still open'),
            chip: { label: t('review', {}, 'Review'), tone: 'red' },
            onPress: eodActive ? () => push('eod') : undefined,
        });
    }
    if (data.voidsToday > 0) {
        attentionItems.push({
            id: 'voids',
            tone: 'amber',
            text: t('n_voids_today', { n: data.voidsToday }, '{n} transaction(s) voided today'),
            chip: { label: String(data.voidsToday), tone: 'amber' },
            onPress: transactionsActive ? () => push('transactions') : undefined,
        });
    }
    if (data.noShowsToday > 0) {
        attentionItems.push({
            id: 'noshows',
            tone: 'amber',
            text: t('n_no_shows', { n: data.noShowsToday }, '{n} reservation no-show(s)'),
            chip: { label: String(data.noShowsToday), tone: 'amber' },
            onPress: reservationsActive ? () => push('reservations') : undefined,
        });
    }

    return (
        <div className="tab-wrap">
            {header}

            <div className="tab-group">
                <VerdictCard
                    label={t('today', {}, 'Today')}
                    sentence={sentence}
                    stats={stats}
                    points={data.trend}
                />
                {showShareBar && (
                    <ShareBar
                        segments={[
                            {
                                id: 'cash',
                                label: t('cash', {}, 'Cash'),
                                value: cash,
                                display: fmtMoney(cash, currency),
                                tone: 'green',
                            },
                            {
                                id: 'card',
                                label: t('card', {}, 'Card'),
                                value: card,
                                display: fmtMoney(card, currency),
                                tone: 'blue',
                            },
                        ]}
                        ariaLabel={t('payment_split', {}, 'Payment split')}
                    />
                )}
            </div>

            <AttentionList items={attentionItems} />

            {cards.length === 0 ? (
                <HubEmptyState t={t} testId="operations-empty" />
            ) : (
                <>
                    <div className="pk-section-label">{t('dig_deeper', {}, 'Dig deeper')}</div>
                    <div className="tab-group" style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                        {cards.map((cardEntry) => {
                            if (!CARD_ICON[cardEntry.id]) return null;
                            const row = buildDigRow(cardEntry, data, t, push);
                            return <CondensedRow key={cardEntry.id} testId={cardEntry.id} {...row} />;
                        })}
                    </div>
                </>
            )}
        </div>
    );
}
